/**
 * LiveTv — 3-pane Live TV browser for the Pro player.
 *
 * Layout (spec: 1a, 2b, 3b + Favorites, 4a, 5a)
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │  header: "Live TV"  ·  PRO badge  ·  X / Y counter        │
 *   ├──────────────────────────┬────────────────────────────────┤
 *   │  NATIVE INLINE PREVIEW   │  Selected channel + ⭐ toggle  │
 *   │  (VLC surface mounted    │  EPG: today's schedule, live   │
 *   │   over the placeholder)  │  programme highlighted         │
 *   ├──────────────────────────┴────────────────────────────────┤
 *   │  CATEGORIES SIDEBAR    │  CHANNEL LIST (logo · name ·     │
 *   │  (Favorites first)     │  next-on-EPG · ⭐ favorite · ▶) │
 *   └────────────────────────┴───────────────────────────────────┘
 *
 * Playback engine: libVLC via the Capacitor `NativePlayer.playInline`
 * bridge (same plugin LiveTvWatch.jsx uses). Hls.js does NOT work for
 * Live IPTV — AC3/EAC3 audio is undecodable in MediaSource, and the
 * worker buffers grow until the WebView OOMs.
 *
 * Click a programme in the past → catch-up via Xtream when available.
 * Click ▶ on a channel row (or double-click the row) → fullscreen
 * native player.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tv, Star, ChevronRight, Play } from "lucide-react";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import { currentProgramme, getChannelProgrammes } from "../lib/epgParser";
import { isFavorited, toggleFavorite } from "../lib/favorites";
import {
  buildXtreamCatchupUrl,
  fetchXtreamLiveCategories,
  fetchXtreamLiveStreams,
} from "../lib/xtreamApi";
import {
  getCachedLiveCategories,
  cacheLiveCategories,
  getCachedLiveStreams,
  cacheLiveStreams,
  ensureEpgLoaded,
} from "../lib/playlistCache";
import {
  isNativePlayerAvailable,
  playInlineNativeExoPlayer,
  updateInlineNativeRect,
  stopInlineNativeExoPlayer,
  playWithNativeExoPlayer,
} from "../lib/nativePlayer";
import { isElectron } from "../lib/deviceId";
import Hls from "hls.js";
import mpegts from "mpegts.js";
import { webUrl } from "../lib/webFetch";
import { makeProxyLoader } from "../lib/hlsProxyLoader";
import PosterImage from "../components/PosterImage";
import { debugLog } from "../lib/debugMode";

const FAV_TYPE = "live_tv";

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function progressPct(prog) {
  if (!prog?.start_ts || !prog?.stop_ts) return 0;
  const now = Math.floor(Date.now() / 1000);
  if (now <= prog.start_ts) return 0;
  if (now >= prog.stop_ts) return 100;
  return Math.round(((now - prog.start_ts) / (prog.stop_ts - prog.start_ts)) * 100);
}

export default function LiveTv() {
  const navigate = useNavigate();
  const location = useLocation();
  const { channels, epgByChannel, xtream, playlist } = usePlaylistData();

  // v1.3.71 — EPG is now skipped on cold-start auto-refresh (saves
  // ~400 ms on first paint). Trigger an on-demand fetch the moment
  // the user lands on this page so the channel grid still has EPG
  // by the time it scrolls.
  useEffect(() => {
    if (!playlist?.id) return;
    if (epgByChannel && Object.keys(epgByChannel).length > 0) return;
    ensureEpgLoaded(playlist).catch(() => { /* best-effort */ });
  }, [playlist, epgByChannel]);

  const liveChannels = useMemo(
    () => (channels || []).filter((c) => c.content_type === "live_tv"),
    [channels],
  );

  // ─── v1.3.66 Phase 3 — Xtream JSON-API Live TV lazy fetch ──────
  //
  // When the active playlist has Xtream creds, we fetch the live
  // channel list directly from `get_live_streams[&category_id=N]`
  // and merge by stream_id with the in-memory `liveChannels` (from
  // M3U). The result surfaces channels that the M3U parser may
  // have dropped on huge playlists (same root-cause family as
  // Phase 1 / Phase 2 — tail-drop on 591k+ line M3Us).
  //
  // Per-category lazy fetch keeps the network payload small
  // (~30 KB per opened category) and IDB-cached at 15 min so
  // popping between categories doesn't re-hit the provider.
  const [xtreamLiveByCat, setXtreamLiveByCat] = useState({}); // category_name → channels[]
  const [xtreamLiveCats, setXtreamLiveCats] = useState(null);

  // Bumped on toggleFavorite so favorites Set recomputes from localStorage.
  const [favTick, setFavTick] = useState(0);

  // ─── categories ─────────────────────────────────────────────
  // v1.3.66 Phase 3 — Bootstrap effect: load Xtream Live TV
  // categories as soon as creds are available, regardless of
  // which category the user has picked. This lets the sidebar
  // surface Xtream-only categories that the M3U parser may have
  // dropped, so they're actually selectable. Without this, the
  // chicken-and-egg problem is: the lazy per-category effect only
  // runs when the user picks a category, but they can't pick a
  // category that isn't shown in the sidebar.
  useEffect(() => {
    if (!xtream?.creds || !playlist?.id) return;
    if (xtreamLiveCats) return; // already loaded this session
    let cancelled = false;
    (async () => {
      let cats = await getCachedLiveCategories(playlist.id);
      if (!cats) {
        try {
          const fresh = await fetchXtreamLiveCategories(xtream.creds);
          if (Array.isArray(fresh)) {
            cats = fresh;
            await cacheLiveCategories(playlist.id, fresh);
          }
        } catch { /* silent */ }
      }
      if (cancelled) return;
      if (cats) setXtreamLiveCats(cats);
    })();
    return () => { cancelled = true; };
  }, [xtream, playlist?.id, xtreamLiveCats]);

  const categories = useMemo(() => {
    const m = new Map();
    for (const c of liveChannels) {
      const k = c.group || "Uncategorized";
      m.set(k, (m.get(k) || 0) + 1);
    }
    // v1.3.66 Phase 3 follow-up — also surface Xtream-only categories
    // (those resolved by `get_live_categories` but not present in the
    // M3U because the parser tail-dropped them). Without this seed,
    // recently-added provider categories would never be selectable
    // in the sidebar even though Phase 3 has lazy-fetch ready for
    // them. Counts default to 0 until the user opens the category
    // and the lazy fetch populates the per-category cache.
    if (Array.isArray(xtreamLiveCats)) {
      for (const c of xtreamLiveCats) {
        const k = c.category_name || "Uncategorized";
        if (!m.has(k)) m.set(k, 0);
      }
    }
    return [...m.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [liveChannels, xtreamLiveCats]);

  const favoriteUrls = useMemo(() => {
    const set = new Set();
    for (const c of liveChannels) {
      if (isFavorited(FAV_TYPE, c.stream_url)) set.add(c.stream_url);
    }
    return set;
  // favTick intentionally listed — localStorage mutations don't trigger React.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveChannels, favTick]);

  // v1.3.50 — Persist the last-viewed Live TV category across sessions
  // so the user lands on whatever they were watching last time instead
  // of bouncing to Favorites every cold-boot.
  const ACTIVE_CAT_KEY = "vupp_livetv_active_category";
  const categorySelectRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState(() => {
    try {
      return localStorage.getItem(ACTIVE_CAT_KEY) || "__favorites__";
    } catch {
      return "__favorites__";
    }
  });
  useEffect(() => {
    try { localStorage.setItem(ACTIVE_CAT_KEY, activeCategory); }
    catch { /* ignore */ }
  }, [activeCategory]);
  useEffect(() => {
    if (activeCategory === "__favorites__" && favoriteUrls.size === 0 && categories[0]) {
      setActiveCategory(categories[0].name);
    }
  }, [favoriteUrls.size, categories, activeCategory]);

  // v1.3.52 — Smart TV remote keyboard handler. Catches D-pad UP /
  // DOWN events in CAPTURE phase (before the WebView's default tab
  // traversal moves focus past the dropdown) and explicitly bridges
  // focus between the channel list and the Category dropdown.
  //
  // Some Smart TV remotes send `e.keyCode === 19` (KEYCODE_DPAD_UP)
  // v1.4.14 — The keyboard focus-warp handler that jumped focus from
  // the first channel row UP to the (now-removed) top Category bar
  // has been retired along with that bar. Scrolling up on the channel
  // list now simply stops at the first row instead of teleporting the
  // user to the top of the screen — matches the user's requested UX.
  //
  // We keep CASE 2 (Down from the Category dropdown → first channel)
  // as a no-op guard in case an old browser cache still has a select
  // element in the DOM. The keydown handler below only intervenes if
  // the focused element IS the (dormant) categorySelectRef; otherwise
  // it defers to default browser behaviour.
  useEffect(() => {
    const onKey = (e) => {
      const isDown = e.key === "ArrowDown" || e.keyCode === 40 || e.keyCode === 20;
      if (!isDown) return;
      const active = document.activeElement;
      if (!active) return;
      if (categorySelectRef.current && active === categorySelectRef.current) {
        const focusables = channelListRef.current
          ? Array.from(
              channelListRef.current.querySelectorAll(
                'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
              ),
            )
          : [];
        if (focusables.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          focusables[0].focus();
        }
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  // v1.3.53 — Long-press OK / Enter / Select (≥800 ms) opens a fullscreen
  // Category picker. Safety net for the D-pad focus skip the user
  // hit on a Smart TV remote — even when the focus order can't reach
  // the dropdown, this popup always works because it's just a key-
  // hold gesture. Press-then-release within 800 ms passes through to
  // the focused element (channel button etc.) as a normal click.
  //
  // SCOPED TO NON-TOUCH DEVICES: gated only on `maxTouchPoints === 0`
  // (no touchscreen) so it works inside the APK on Android TV / Fire TV
  // *and* the desktop browser preview. Phones / tablets still get the
  // dropdown only — long-pressing a row would be annoying on touch.
  const [categoryPopupOpen, setCategoryPopupOpen] = useState(false);
  // Live remaining-time indicator for the on-screen "Hold OK…" hint.
  const [holdProgress, setHoldProgress] = useState(0); // 0–1, 0 = idle
  useEffect(() => {
    const hasTouch =
      typeof navigator !== "undefined" && (navigator.maxTouchPoints || 0) > 0;
    if (hasTouch) return undefined; // phones / tablets → dropdown only

    const LONG_PRESS_MS = 800;
    let pressedAt = 0;
    let triggered = false;
    let timer = null;
    let progressTimer = null;
    // Comprehensive OK / Select / Enter / D-pad-center detection across
    // every Smart TV remote we've seen in the wild. Samsung Tizen sends
    // keyCode 13, LG WebOS sends 13 too, Android TV native sends 23
    // (KEYCODE_DPAD_CENTER), Xbox controllers send 13/32 + keyCode 96
    // (BUTTON_A), some Hisense remotes send keyCode 65376, and an
    // increasing number of Bluetooth remotes use `code === "Enter"` or
    // "NumpadEnter".
    const isOkKey = (e) => {
      if (!e) return false;
      const k = (e.key || "").toLowerCase();
      const code = (e.code || "").toLowerCase();
      const kc = e.keyCode ?? e.which ?? 0;
      return (
        k === "enter" ||
        k === " " ||
        k === "spacebar" ||
        k === "select" ||
        k === "go" ||
        code === "enter" ||
        code === "numpadenter" ||
        code === "space" ||
        kc === 13 ||  // Enter / Tizen / WebOS
        kc === 32 ||  // Space
        kc === 23 ||  // Android KEYCODE_DPAD_CENTER
        kc === 96 ||  // Game controller BUTTON_A
        kc === 160 || // Numpad Enter
        kc === 65376  // Hisense / some VIDAA remotes
      );
    };
    const startProgressTick = () => {
      const t0 = Date.now();
      progressTimer = setInterval(() => {
        const elapsed = Date.now() - t0;
        const p = Math.min(1, elapsed / LONG_PRESS_MS);
        setHoldProgress(p);
        if (p >= 1) {
          clearInterval(progressTimer);
          progressTimer = null;
        }
      }, 50);
    };
    const stopProgressTick = () => {
      if (progressTimer) { clearInterval(progressTimer); progressTimer = null; }
      setHoldProgress(0);
    };
    const onDown = (e) => {
      if (!isOkKey(e)) return;
      if (categoryPopupOpen) return; // already open — don't re-trigger
      if (pressedAt) return; // already tracking — ignore key-repeat
      pressedAt = Date.now();
      triggered = false;
      startProgressTick();
      timer = setTimeout(() => {
        triggered = true;
        stopProgressTick();
        setCategoryPopupOpen(true);
      }, LONG_PRESS_MS);
    };
    const onUp = (e) => {
      if (!isOkKey(e)) return;
      pressedAt = 0;
      if (timer) { clearTimeout(timer); timer = null; }
      stopProgressTick();
      if (triggered) {
        // Long-press already fired — eat this keyup so the focused
        // <button> doesn't ALSO get clicked when the user releases.
        e.preventDefault();
        e.stopPropagation();
        triggered = false;
      }
    };
    // Cancel timer if focus leaves the window (user pressed BACK, app
    // backgrounded, etc.) so we don't fire a stale popup later.
    const onBlur = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      stopProgressTick();
      pressedAt = 0;
      triggered = false;
    };
    window.addEventListener("keydown", onDown, true);
    window.addEventListener("keyup", onUp, true);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onDown, true);
      window.removeEventListener("keyup", onUp, true);
      window.removeEventListener("blur", onBlur);
      if (timer) clearTimeout(timer);
      if (progressTimer) clearInterval(progressTimer);
    };
  }, [categoryPopupOpen]);

  // v1.3.66 Phase 3 — Xtream Live TV lazy fetch for active category.
  // Mirrors the Movies pattern from Phase 2:
  //   1. Resolve get_live_categories once per session (24h cache).
  //   2. Map the active category NAME → category_id by exact match.
  //   3. Hit get_live_streams?category_id=N (15-min IDB cache).
  //   4. Persist + setState so `channelsForCategory` swaps in the
  //      Xtream list when available.
  useEffect(() => {
    if (!xtream?.creds || !playlist?.id) return undefined;
    if (
      !activeCategory ||
      activeCategory === "__favorites__"
    ) return undefined;
    let cancelled = false;
    const plId = playlist.id;
    const creds = xtream.creds;
    (async () => {
      // 1. Resolve categories.
      let cats = xtreamLiveCats;
      if (!cats) {
        cats = await getCachedLiveCategories(plId);
        if (!cats) {
          try {
            const fresh = await fetchXtreamLiveCategories(creds);
            if (Array.isArray(fresh)) {
              cats = fresh;
              await cacheLiveCategories(plId, fresh);
            }
          } catch { /* silent */ }
        }
        if (cancelled) return;
        if (cats) setXtreamLiveCats(cats);
      }
      if (!cats || cats.length === 0) return;
      // 2. Name → category_id.
      let targetCatId = null;
      let targetCatName = null;
      if (activeCategory !== "Uncategorized") {
        const match = cats.find((c) => c.category_name === activeCategory);
        if (match) {
          targetCatId = match.category_id;
          targetCatName = match.category_name;
        }
      }
      if (!targetCatId) return;
      // 3. IDB cache hit (instant).
      const cached = await getCachedLiveStreams(plId, targetCatId);
      if (cancelled) return;
      if (Array.isArray(cached) && cached.length > 0) {
        setXtreamLiveByCat((prev) => ({ ...prev, [activeCategory]: cached }));
      }
      // 4. Live refresh.
      try {
        const fresh = await fetchXtreamLiveStreams(
          creds,
          targetCatId,
          undefined,
          targetCatName,
        );
        if (cancelled) return;
        if (Array.isArray(fresh) && fresh.length > 0) {
          setXtreamLiveByCat((prev) => ({ ...prev, [activeCategory]: fresh }));
          await cacheLiveStreams(plId, targetCatId, fresh);
        }
      } catch { /* silent — fall back to M3U */ }
    })();
    return () => { cancelled = true; };
  }, [playlist?.id, activeCategory, xtream, xtreamLiveCats]);

  const channelsForCategory = useMemo(() => {
    if (activeCategory === "__favorites__") {
      return liveChannels.filter((c) => favoriteUrls.has(c.stream_url));
    }
    const m3uList = liveChannels.filter(
      (c) => (c.group || "Uncategorized") === activeCategory,
    );
    // v1.3.66 Phase 3 — merge with Xtream JSON list when present.
    // De-dupe by xc_id (preferred) then stream_url. Channels from
    // the Xtream list win because they're sourced direct from the
    // provider DB and won't be missing the tail of the M3U file.
    const xtList = xtreamLiveByCat[activeCategory];
    if (!Array.isArray(xtList) || xtList.length === 0) return m3uList;
    const seen = new Set();
    const out = [];
    for (const row of xtList) {
      const key = row.xc_id ? `xc:${row.xc_id}` : `u:${row.stream_url}`;
      if (seen.has(key)) continue;
      seen.add(key);
      // Merge whatever metadata the M3U pass already enriched
      // (epg_channel_id, custom logo, tv_archive flags) when the
      // same channel exists on both sides. Xtream wins on raw
      // identity fields (stream_url, xc_id) so newly added
      // channels show up even when the M3U missed them.
      const m3uMatch =
        m3uList.find(
          (c) =>
            (row.xc_id && String(c.xc_id) === String(row.xc_id)) ||
            c.stream_url === row.stream_url,
        ) || null;
      // v1.4.0 — CRITICAL: preserve `m3uMatch.id` when merging.
      // The Xtream row's own `id` field (a numeric string) would
      // otherwise overwrite the stable `c-{order}-{hash}` id that
      // `LiveTvWatch` uses to look the channel back up on the
      // `/player/live/:channelId` route. Without this preservation,
      // Electron/Web clicks land on "Channel not found" while Android
      // works fine (Android bypasses LiveTvWatch via native ExoPlayer).
      out.push(m3uMatch
        ? { ...m3uMatch, ...row, id: m3uMatch.id, logo_url: row.logo_url || m3uMatch.logo_url }
        : row);
    }
    // Append M3U-only channels (different xc_id / stream_url).
    for (const c of m3uList) {
      const key = c.xc_id ? `xc:${c.xc_id}` : `u:${c.stream_url}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(c);
      }
    }
    return out;
  }, [liveChannels, activeCategory, favoriteUrls, xtreamLiveByCat]);

  // ─── selection ──────────────────────────────────────────────
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    // v1.4.34 — Match by `stream_url` (STABLE across playlist re-imports),
    // NOT `id` (regenerated by IdbPutPlaylist on every playlist write).
    // Previously the Chan_Store background rewrite would flip `selected`
    // back to `channelsForCategory[0]` mid-key-press because the same
    // channel had a new `id`, so the user's D-pad Down "worked" but
    // instantly snapped back to the first row. Debug capture caught this
    // 30 959-row IDB rewrite firing in the middle of the user's key
    // presses.
    if (
      !selected ||
      !channelsForCategory.find(
        (c) => c.stream_url === selected.stream_url || c.id === selected.id,
      )
    ) {
      setSelected(channelsForCategory[0] || null);
    }
  }, [channelsForCategory, selected]);

  // v1.4.0 rev 8 — Auto-preview restored on NATIVE (Android) only.
  //
  // The rev-7 rule ("no auto-open") was correct for Web/Electron where
  // the inline surface uses hls.js and the fullscreen path is a new
  // route (two connections during navigation). It was TOO strict for
  // Android, where:
  //   • the inline SurfaceView reuses one ExoPlayer/VLC instance
  //   • fullscreen opens a native Activity that we can gate behind an
  //     explicit `stopInline() → wait for socket release → play()` step
  //
  // Contract:
  //   • Native (Android): `previewChannelId` auto-tracks `selected.id`
  //     as the user arrow-keys through. Each new selection stops the
  //     previous surface FIRST (see the sequencing effect further down)
  //     so exactly ONE provider connection is ever open.
  //   • Non-native (Web/Electron): stays explicit-click — the Preview
  //     button is what triggers `setPreviewChannelId`.
  // v1.4.38 — `previewChannelId` semantically holds the STREAM_URL now
  // (rename kept for backward compat). The `id` field is regenerated
  // by IdbPutPlaylist on every playlist rewrite, so a numeric id stored
  // here becomes stale within seconds and the previewChannel lookup
  // returns null. stream_url is stable across imports.
  const [previewChannelId, setPreviewChannelId] = useState(null);
  const previewChannel = useMemo(
    () =>
      channelsForCategory.find(
        (c) => c.stream_url === previewChannelId || c.id === previewChannelId,
      ) || null,
    [channelsForCategory, previewChannelId],
  );

  // ─── EPG ────────────────────────────────────────────────────
  const programmes = useMemo(() => {
    if (!selected) return [];
    return (getChannelProgrammes(epgByChannel, selected) || []).slice(0, 12);
  }, [selected, epgByChannel]);
  const nowProg = useMemo(() => {
    if (!selected) return null;
    return currentProgramme(epgByChannel, selected);
  }, [selected, epgByChannel]);

  // v1.3.28 — first future programme on this channel, used to flag the
  // "NEXT" pill in the EPG list. Pulled from the same `programmes`
  // slice we already filtered to 12 items.
  const nextProgStartTs = useMemo(() => {
    if (!nowProg || !programmes?.length) return null;
    const idx = programmes.findIndex((p) => p.start_ts === nowProg.start_ts);
    return idx >= 0 && idx + 1 < programmes.length ? programmes[idx + 1].start_ts : null;
  }, [nowProg, programmes]);

  // v1.3.68 — `catchupItems` memo + horizontal strip removed at
  // user request. Catch-up playback is still triggered by clicking
  // a past programme in the EPG list (see the EPG click handler
  // around line ~594 below).

  // ─── NATIVE inline preview ──────────────────────────────────
  // Mirrors LiveTvWatch.jsx — place a Capacitor SurfaceView over the
  // placeholder div so libVLC renders inline (AC3/EAC3 decoded native).
  // On Android/Electron the surface auto-mounts to the currently `selected`
  // channel via a debounced effect (see below). On plain Web the preview
  // requires an explicit Preview-button click.
  const placeholderRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const mpegtsRef = useRef(null);
  const native = isNativePlayerAvailable();
  const electron = isElectron();
  // v1.4.7 — Web Player mode. When set, this browser tab is running
  // the same player UI as Electron / Android. We enable auto-preview
  // via hls.js (HLS) or mpegts.js (raw .ts) and skip any mpv/native
  // hand-off logic that isn't available in a plain browser.
  const webPlayer = typeof window !== "undefined" && window.__ZAPP_IS_WEBPLAYER__ === true;
  const autoPreview = native || electron || webPlayer;
  const canInlinePlay = electron || webPlayer;
  const isHlsStream = useMemo(
    () => previewChannel?.stream_url && /\.m3u8(?:\?|$)/i.test(previewChannel.stream_url),
    [previewChannel?.stream_url]
  );

  // Guard shared with the fullscreen-open path so a `stopThenStart`
  // in flight can be cancelled if the user taps Watch mid-preview.
  const previewGenRef = useRef(0);

  // v1.4.4 — Suppress the inline hls.js preview while mpv is playing
  // fullscreen. Without this, when mpv takes over the screen the
  // Electron window loses focus → `visibilitychange` fires → the
  // preview effect restarts hls.js on the SAME stream URL → the
  // provider's 1-connection cap kicks in → mpv gets an empty stream
  // and shows the black screen the user reported.
  const mpvActiveRef = useRef(false);

  // Listen for mpv exit so we know when to re-enable the inline
  // preview restart. Main.js broadcasts `mpv:exit` on exit / error.
  useEffect(() => {
    if (!electron || !window.electronAPI?.onMpvExit) return undefined;
    const off = window.electronAPI.onMpvExit(() => {
      mpvActiveRef.current = false;
    });
    return () => { if (typeof off === "function") off(); };
  }, [electron]);

  const stopElectronPreview = useCallback(() => {
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch {/* ignore */}
      hlsRef.current = null;
    }
    if (mpegtsRef.current) {
      try {
        mpegtsRef.current.unload();
        mpegtsRef.current.detachMediaElement();
        mpegtsRef.current.destroy();
      } catch {/* ignore */}
      mpegtsRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      } catch {/* ignore */}
    }
  }, []);

  const startInline = useCallback(async () => {
    const el = placeholderRef.current;
    if (!el || !previewChannel) return;
    const r = el.getBoundingClientRect();
    if (r.width < 4 || r.height < 4) return;
    if (native) {
      const rect = { x: r.left, y: r.top, width: r.width, height: r.height };
      await playInlineNativeExoPlayer(previewChannel.stream_url, rect, {
        title: previewChannel.name,
        contentType: "live_tv",
      });
    } else if (canInlinePlay && isHlsStream) {
      // Electron/Web: hls.js on the inline <video>. Non-HLS (raw .ts)
      // falls through to mpegts.js below (Web) or mpv (Electron).
      const videoEl = videoRef.current;
      if (!videoEl) return;
      if (Hls.isSupported()) {
        const hls = new Hls({
          lowLatencyMode: false,
          enableWorker: true,
          backBufferLength: 30,
          maxBufferLength: 20,
          maxMaxBufferLength: 45,
          // v1.4.8 — Route HLS manifest + segments through the backend
          // CORS proxy in Web Player mode. hls.js hits both the .m3u8
          // and each .ts chunk cross-origin; without this the inline
          // preview 404/CORS-fails before it starts.
          loader: makeProxyLoader(),
        });
        hlsRef.current = hls;
        hls.loadSource(previewChannel.stream_url);
        hls.attachMedia(videoEl);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          videoEl.play().catch(() => {/* autoplay blocked */});
        });
      } else if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
        videoEl.src = previewChannel.stream_url;
        videoEl.play().catch(() => {/* autoplay blocked */});
      }
    } else if (webPlayer && !isHlsStream) {
      // v1.4.7 — Web Player MPEG-TS preview via mpegts.js. This gives
      // us AAC/H.264 raw .ts streams inline without a native player.
      // (AC3/EAC3 audio still won't decode — user must fall back to
      // the Android or Windows app for those.)
      const videoEl = videoRef.current;
      if (!videoEl) return;
      try {
        if (!mpegts.getFeatureList().mseLivePlayback) return;
        // v1.4.8 — Route the raw MPEG-TS URL through the backend proxy
        // in Web Player mode so cross-origin providers don't CORS-fail.
        const player = mpegts.createPlayer(
          { type: "mpegts", isLive: true, url: webUrl(previewChannel.stream_url) },
          { enableWorker: true, enableStashBuffer: false, liveBufferLatencyChasing: true }
        );
        mpegtsRef.current = player;
        player.attachMediaElement(videoEl);
        player.load();
        player.play().catch(() => {/* autoplay blocked */});
      } catch {/* ignore — user can still click Watch */}
    }
  }, [previewChannel, native, canInlinePlay, webPlayer, isHlsStream]);

  const measureAndSync = useCallback(async (mode) => {
    if (mode === "start") {
      await startInline();
      return;
    }
    // Only the native SurfaceView needs its rect updated on layout
    // changes — the Electron <video> flows with CSS naturally.
    if (!native) return;
    const el = placeholderRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width < 4 || r.height < 4) return;
    await updateInlineNativeRect({ x: r.left, y: r.top, width: r.width, height: r.height });
  }, [startInline, native]);

  // Mount / remount the preview surface for the current `previewChannel`.
  // Awaits stopInline so the previous socket is truly released before
  // we open the next one — enforces the one-connection guarantee even
  // if the plugin's stop is async.
  useEffect(() => {
    if (!autoPreview || !previewChannel) return undefined;
    if (mpvActiveRef.current) return undefined; // v1.4.4 — mpv holds the connection
    const gen = ++previewGenRef.current;
    let cancelled = false;
    (async () => {
      if (native) {
        try { await stopInlineNativeExoPlayer(); } catch {/* noop */}
      } else if (canInlinePlay) {
        stopElectronPreview();
      }
      // Small settling delay so the previous HTTP socket has time to
      // actually close on the OS network stack. Empirically 200ms is
      // enough for a single-connection provider not to see two clients.
      await new Promise((r) => setTimeout(r, 220));
      if (cancelled || previewGenRef.current !== gen) return;
      if (mpvActiveRef.current) return; // recheck — mpv may have started
      await startInline();
    })();
    return () => {
      cancelled = true;
      if (native) stopInlineNativeExoPlayer();
      else if (canInlinePlay) stopElectronPreview();
    };
  }, [autoPreview, native, canInlinePlay, previewChannel?.id, previewChannel, startInline, stopElectronPreview]);

  // Keep the surface aligned on scroll / resize / orientation.
  useEffect(() => {
    if (!native || !previewChannel) return undefined;
    const onLayout = () => measureAndSync("update");
    const ro = new ResizeObserver(onLayout);
    if (placeholderRef.current) ro.observe(placeholderRef.current);
    window.addEventListener("scroll", onLayout, true);
    window.addEventListener("resize", onLayout);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", onLayout, true);
      window.removeEventListener("resize", onLayout);
    };
  }, [native, previewChannel, measureAndSync]);

  // Resume after the user returns from a fullscreen native activity.
  // Only re-mounts when there IS an active preview.
  useEffect(() => {
    if (!autoPreview || !previewChannel) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (mpvActiveRef.current) return; // v1.4.4 — mpv still playing
      requestAnimationFrame(() => {
        if (document.visibilityState === "visible" && !mpvActiveRef.current) {
          measureAndSync("start");
        }
      });
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [autoPreview, previewChannel?.id, measureAndSync, previewChannel]);

  // Stop the inline surface / hls when this page unmounts.
  useEffect(() => () => {
    if (native) stopInlineNativeExoPlayer();
    if (canInlinePlay) stopElectronPreview();
  }, [native, canInlinePlay, stopElectronPreview]);

  // v1.3.56 — When the category popup opens, tear down the inline native
  // surface so it doesn't render OVER the popup. Re-mounts on close if
  // there is an active preview.
  useEffect(() => {
    if (!native || !previewChannel) return;
    if (categoryPopupOpen) {
      stopInlineNativeExoPlayer();
    } else {
      const h = requestAnimationFrame(() => measureAndSync("start"));
      return () => cancelAnimationFrame(h);
    }
    return undefined;
  }, [categoryPopupOpen, native, previewChannel, measureAndSync]);

  // v1.4.0 rev 8 — NATIVE + ELECTRON: auto-track the currently `selected`
  // channel into `previewChannelId` with a 350 ms debounce. The debounce
  // ensures that quickly arrow-keying through 10 channels does NOT
  // open 10 sockets — only the channel the user pauses on is opened.
  //
  // v1.4.76 — Web Player is opt-in: arrow-keying through the list now
  // ONLY moves the highlight and never auto-opens a stream. The user
  // must press Enter (or click) once to start the inline preview, and
  // press Enter (or click) a second time to go fullscreen. This matches
  // the classic Smart TV EPG pattern and stops the "channel starts
  // playing as soon as I nudge the remote" complaint.
  useEffect(() => {
    if (!autoPreview) return undefined;
    // v1.1.2 — Native (Android/TV) is now opt-in like webPlayer:
    // arrow-keying through the list only moves the highlight and
    // NEVER auto-opens a stream. Fixes the "channel starts playing
    // as soon as I nudge the remote" complaint on Smart TV. First
    // Enter opens the inline preview (see handleChannelActivate);
    // second Enter goes fullscreen. Electron desktop preserves the
    // classic auto-preview behaviour (mouse hover moves highlight
    // less aggressively than arrow keys, so it's still nice).
    if (native || webPlayer) return undefined;
    if (!selected) {
      setPreviewChannelId(null);
      return undefined;
    }
    if (selected.stream_url === previewChannelId) return undefined;
    // While the user is still scrolling, tear the current preview down
    // immediately so the OLD socket closes right away.
    if (native) stopInlineNativeExoPlayer();
    else if (canInlinePlay) stopElectronPreview();
    const t = setTimeout(() => setPreviewChannelId(selected.stream_url), 350);
    return () => clearTimeout(t);
  }, [autoPreview, native, canInlinePlay, webPlayer, selected, previewChannelId, stopElectronPreview]);

  // ─── handlers ───────────────────────────────────────────────
  const onProgrammeClick = useCallback((prog) => {
    if (!selected || !prog) return;
    const nowTs = Math.floor(Date.now() / 1000);
    if (prog.stop_ts > nowTs) return; // future / live → no catch-up
    if (xtream?.creds && selected.xc_id && selected.tv_archive) {
      const duration = Math.max(1, prog.stop_ts - prog.start_ts);
      const built = buildXtreamCatchupUrl(
        xtream.creds,
        selected.xc_id,
        prog.start_ts,
        duration,
      );
      if (built?.primary) {
        navigate("/player/watch", {
          state: {
            stream_url: built.primary,
            fallback_url: built.fallback,
            name: `${selected.name} — ${prog.title || "Catch-up"}`,
            logo: selected.logo_url || "",
            type: "catchup",
          },
        });
      }
    }
  }, [selected, xtream, navigate]);

  // v1.4.10 — Web Player fullscreen: request the browser's Fullscreen
  // API on the inline <video>. Works on both the browser Web Player
  // (`/admin/webplayer`) and the Windows Web Player kiosk (Electron
  // shell wrapping the same route). Called in a click handler so the
  // user-gesture requirement of the Fullscreen API is satisfied.
  const requestVideoFullscreen = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const req =
      v.requestFullscreen ||
      v.webkitRequestFullscreen ||
      v.mozRequestFullScreen ||
      v.msRequestFullscreen ||
      // iOS Safari: only the video element itself has the WebKit
      // presentation API, no vanilla requestFullscreen.
      v.webkitEnterFullscreen;
    if (typeof req === "function") {
      try { req.call(v); } catch {/* user cancelled / not allowed */}
    }
  }, []);

  // Fullscreen native player — launched from ▶ button OR double-click.
  // v1.4.0 rev 8 — Strict single-connection handover:
  //   1. Bump `previewGenRef` so any in-flight `stopThenStart` inside the
  //      preview effect gets cancelled before it opens a new socket.
  //   2. Tear down the CURRENT engine (native surface OR hls.js) and
  //      await its stop.
  //   3. 220 ms settling delay for the OS network stack to drop the TCP
  //      socket (some providers count the socket, not the app instance).
  //   4. Launch the fullscreen engine (native ExoPlayer / mpv). The
  //      native/mpv layers ALSO kill any lingering fullscreen instance
  //      before spawning — belt + braces.
  // When the fullscreen engine exits, `visibilitychange` re-mounts the
  // inline preview for the still-selected channel — same stop→wait→
  // start dance handled by the preview effect.
  const onChannelOpen = useCallback(async (ch) => {
    if (!ch) return;
    previewGenRef.current += 1; // cancel any pending preview start
    if (native) {
      try { await stopInlineNativeExoPlayer(); } catch {/* noop */}
      await new Promise((r) => setTimeout(r, 220));
      const r = await playWithNativeExoPlayer(ch.stream_url, {
        title: ch.name,
        contentType: "live_tv",
      });
      if (r?.ok) return;
    } else if (electron && window.electronAPI?.playWithMpv) {
      // Windows: destroy inline hls.js FIRST, wait for the browser to
      // FIN the TCP connection, then spawn mpv. Guarantees exactly one
      // provider socket during the handover.
      mpvActiveRef.current = true; // suppress preview restart on visibility loss
      stopElectronPreview();
      await new Promise((r) => setTimeout(r, 220));
      try {
        await window.electronAPI.playWithMpv(ch.stream_url, { title: ch.name });
      } catch {
        mpvActiveRef.current = false;
      }
      return;
    } else if (webPlayer) {
      // v1.4.10 — Web Player: play inline via hls.js / mpegts.js AND
      // request browser fullscreen on the <video>. Skips the previous
      // navigate to `/player/live/{id}` intermediate screen entirely.
      // We synchronously set selected + previewChannelId so the preview
      // effect starts loading the stream without the 350 ms debounce,
      // and we fire the Fullscreen API in the same click tick so the
      // browser accepts it as a user gesture.
      setSelected(ch);
      setPreviewChannelId(ch.id);
      requestVideoFullscreen();
      return;
    }
    // Plain Web (admin browsing, no webplayer flag) — fall through to
    // the React Live watch page. Kept for completeness; the webplayer
    // guard above catches the actual streaming clients.
    navigate(`/player/live/${encodeURIComponent(ch.id)}`);
  }, [native, electron, webPlayer, navigate, stopElectronPreview, requestVideoFullscreen]);

  // v1.4.76 / v1.1.2 — Two-step activation for the D-pad / mouse:
  //   • 1st click / Enter  → start the inline preview (no fullscreen)
  //   • 2nd click / Enter  → go fullscreen (via onChannelOpen)
  // Any activation on a DIFFERENT channel counts as a first press again.
  // Timing window matches typical Smart-TV double-press UX (~450 ms).
  //
  // v1.1.2 — Extended from webPlayer-only to also cover NATIVE (Android)
  // so scrolling down the channel list on a Smart TV remote no longer
  // auto-opens a stream. Electron desktop keeps legacy one-tap.
  const lastActivateRef = useRef({ url: null, ts: 0 });
  const handleChannelActivate = useCallback((ch) => {
    if (!ch) return;
    const twoStep = webPlayer || native;
    if (!twoStep) {
      // Electron desktop keeps the legacy "one-tap → open" behaviour.
      onChannelOpen(ch);
      return;
    }
    const now = Date.now();
    const prev = lastActivateRef.current;
    const isDouble = prev.url === ch.stream_url && (now - prev.ts) < 450;
    lastActivateRef.current = { url: ch.stream_url, ts: now };
    if (isDouble) {
      onChannelOpen(ch); // fullscreen
      return;
    }
    // First press → inline preview only.
    setSelected(ch);
    setPreviewChannelId(ch.stream_url);
  }, [webPlayer, native, onChannelOpen]);

  // v1.4.10 — Web Player auto-open from Browse.jsx.
  // When the user clicks a live-TV tile in Browse, the click handler
  // navigates HERE with `state.autoOpenChannelId` set. We wait for
  // the full `liveChannels` list to hydrate, find the target channel,
  // switch to its category if we're not already on it, and call
  // `onChannelOpen(channel)` — same code path as a direct row click.
  // Chrome's sticky user-activation window (~5 s after the click) still
  // covers us so `requestVideoFullscreen()` inside `onChannelOpen`
  // succeeds without a new gesture.
  const autoOpenDoneRef = useRef(false);
  useEffect(() => {
    if (!webPlayer) return;
    if (autoOpenDoneRef.current) return;
    const targetId = location.state?.autoOpenChannelId;
    if (!targetId) return;
    if (!Array.isArray(liveChannels) || liveChannels.length === 0) return;
    const target = liveChannels.find(
      (c) => c && (c.id === targetId || String(c.xc_id) === String(targetId)),
    );
    if (!target) return;
    autoOpenDoneRef.current = true;
    // Switch category if the target lives elsewhere.
    const targetCat = target.group || "Uncategorized";
    if (activeCategory !== targetCat && activeCategory !== "__favorites__") {
      setActiveCategory(targetCat);
    }
    // Scrub location.state so a subsequent refresh / back-navigation
    // doesn't re-fire the auto-open loop.
    try {
      navigate(location.pathname, { replace: true, state: null });
    } catch { /* ignore */ }
    onChannelOpen(target);
  }, [webPlayer, liveChannels, location.state, location.pathname, navigate, onChannelOpen, activeCategory]);

  const onToggleFav = useCallback((ch, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    toggleFavorite(FAV_TYPE, {
      stream_url: ch.stream_url,
      name: ch.name,
      logo_url: ch.logo_url,
      group: ch.group,
    });
    setFavTick((t) => t + 1);
  }, []);

  const selectedIsFav = selected ? favoriteUrls.has(selected.stream_url) : false;

  // ─── v1.3.28 polish #1: keyboard ↑/↓ nav between channels ──
  // Critical for TV-remote users. When no input/textarea is focused,
  // Arrow Up/Down moves the selection within `channelsForCategory`,
  // ENTER opens fullscreen.
  //
  // v1.4.28-k — Bug fix: on Android TV WebView, `setSelected(next)`
  // alone changed the visual highlight but native DOM focus stayed
  // on the OLD button. After a few Down presses the WebView's
  // spatial-nav heuristic would then reject any further ArrowDown
  // because it thought the focused button was already the "last
  // visible" one in the viewport. The user saw the selection
  // freeze part-way down the list. Fix: after every setSelected,
  // move DOM focus to the button matching the new channel id so
  // native focus stays in sync with React state.
  const channelListRef = useRef(null);
  useEffect(() => {
    function focusChannel(ch) {
      const root = channelListRef.current;
      if (!root || !ch) return;
      // v1.4.38 — Prefer stream_url (STABLE across IdbPutPlaylist
      // rewrites). Fall back to id for backwards compat.
      let btn = null;
      if (ch.stream_url) {
        btn = root.querySelector(
          `[data-stream-url="${CSS.escape(ch.stream_url)}"]`,
        );
      }
      if (!btn && ch.id) {
        btn = root.querySelector(`[data-testid="pro-live-row-${ch.id}"]`);
      }
      try { btn?.focus({ preventScroll: false }); } catch { btn?.focus?.(); }
      try { btn?.scrollIntoView({ block: "nearest" }); } catch { /* older WebViews */ }
    }
    function onKey(e) {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      // v1.3.55 — DON'T hijack Enter when focus is on a non-channel
      // control. Previously this handler fired `onChannelOpen(selected)`
      // for every Enter keypress anywhere on the page, so pressing OK
      // on the Category dropdown / Browse button / popup items all
      // bounced the user into fullscreen playback of the highlighted
      // channel instead of opening the dropdown or the picker.
      //
      // Allow this shortcut ONLY when:
      //   • focus is inside the channel list, OR
      //   • nothing is focused (e.g. cold-loaded page on a Smart TV
      //     where no element grabbed focus yet — body / html is the
      //     activeElement).
      if (categoryPopupOpen) return;
      if (t && t !== document.body && t !== document.documentElement) {
        const insideChannelList = channelListRef.current?.contains(t);
        if (!insideChannelList) return;
      }
      if (!channelsForCategory.length) return;
      // v1.4.32 — Some Google TV / Sony / TCL remotes emit
      //   • KEYCODE_DPAD_DOWN  → e.keyCode === 40  (typical Chromium)
      //   • KEYCODE_DPAD_DOWN  → e.keyCode === 20  (Fire TV, some Sony)
      //   • KEYCODE_MEDIA_NEXT → e.keyCode === 22  (very old Android TV)
      // and `e.key` may be "ArrowDown", "Down", or missing depending on
      // the WebView build. Match all known permutations.
      const isDown =
        e.key === "ArrowDown" || e.key === "Down" ||
        e.keyCode === 40 || e.keyCode === 20;
      const isUp =
        e.key === "ArrowUp" || e.key === "Up" ||
        e.keyCode === 38 || e.keyCode === 19;
      const isEnter =
        e.key === "Enter" || e.key === "Select" ||
        e.keyCode === 13 || e.keyCode === 23; // 23 = KEYCODE_DPAD_CENTER
      // v1.4.37 — Look up the current index BY STABLE STREAM_URL first
      // (id is regenerated by IdbPutPlaylist on every playlist rewrite,
      // so the stale `selected.id` doesn't match the freshly-computed
      // channelsForCategory entries even when the same channel is
      // still in the list). Fall back to id for backwards compat.
      let idx = -1;
      if (selected) {
        idx = channelsForCategory.findIndex((c) => c.stream_url === selected.stream_url);
        if (idx < 0) idx = channelsForCategory.findIndex((c) => c.id === selected.id);
      }
      // v1.4.34 — Log every keydown the channel list receives so we can
      // diagnose which keycode the user's Smart TV remote is actually
      // emitting. Only logs when debug mode is on (activation key
      // "logtest") so production users don't see this.
      try {
        debugLog(
          `[livetv-key] key="${e.key}" code=${e.keyCode} isDown=${isDown} isUp=${isUp} isEnter=${isEnter} idx=${idx} sel_url=${selected?.stream_url?.slice(-40) ?? "null"} chan_count=${channelsForCategory.length}`,
        );
      } catch { /* ignore */ }
      if (isDown) {
        e.preventDefault();
        e.stopPropagation();
        const next = channelsForCategory[Math.min(channelsForCategory.length - 1, idx + 1)];
        if (next) {
          setSelected(next);
          focusChannel(next);
        }
      } else if (isUp) {
        e.preventDefault();
        e.stopPropagation();
        const prev = channelsForCategory[Math.max(0, idx - 1)];
        if (prev) {
          setSelected(prev);
          focusChannel(prev);
        }
      } else if (isEnter && selected) {
        e.preventDefault();
        // v1.4.76 — Two-step activation on webPlayer (single = preview
        // inline, double = fullscreen). Non-webPlayer keeps direct open.
        if (webPlayer) handleChannelActivate(selected);
        else onChannelOpen(selected);
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [channelsForCategory, selected, onChannelOpen, categoryPopupOpen, webPlayer, handleChannelActivate]);

  // Auto-scroll the selected channel into view so keyboard nav doesn't
  // navigate the focus off-screen. v1.4.38 — key on stream_url (stable
  // across IDB re-imports) with an id fallback.
  useEffect(() => {
    if (!selected || !channelListRef.current) return;
    const root = channelListRef.current;
    let el = null;
    if (selected.stream_url) {
      el = root.querySelector(
        `[data-stream-url="${CSS.escape(selected.stream_url)}"]`,
      );
    }
    if (!el && selected.id) {
      el = root.querySelector(`[data-channel-id="${selected.id}"]`);
    }
    el?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selected]);

  // ─── v1.3.28 polish #3: drag-to-resize sidebar ──
  // Mouse / touch drag adjusts the width; focus + ←/→ does the same
  // in 24 px steps (TV-remote friendly). Persists in localStorage.
  const SIDEBAR_MIN = 140;
  const SIDEBAR_MAX = 380;
  const [sidebarW, setSidebarW] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("vupp_livetv_sidebar_w") || "220", 10);
      if (Number.isFinite(v) && v >= SIDEBAR_MIN && v <= SIDEBAR_MAX) return v;
    } catch {/* noop */}
    return 220;
  });
  useEffect(() => {
    try { localStorage.setItem("vupp_livetv_sidebar_w", String(sidebarW)); } catch {/* noop */}
  }, [sidebarW]);
  const dragRef = useRef(null);
  const onDragStart = useCallback((e) => {
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startW = sidebarW;
    const onMove = (ev) => {
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const w = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, startW + (x - startX)));
      setSidebarW(w);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
  }, [sidebarW]);
  const onDragKey = useCallback((e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setSidebarW((w) => Math.max(SIDEBAR_MIN, w - 24));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setSidebarW((w) => Math.min(SIDEBAR_MAX, w + 24));
    }
  }, []);


  return (
    // v1.3.29 — root used `h-full`, which collapsed to content height
    // because the PlayerLayout shell only enforces `min-h-screen`. That
    // broke the `flex-1 min-h-0` chain below, so the categories and
    // channel lists had no concrete height for their `overflow-y-auto`
    // to overflow out of. Anchor to the dynamic viewport height minus
    // the sticky top-bar (≈64 px) so the lists always have a real box
    // to scroll inside.
    <div
      className="flex flex-col text-white overflow-hidden"
      style={{ height: "calc(100dvh - 64px)" }}
      data-testid="pro-live-tv"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Tv className="w-4 h-4 text-[#22d3ee]" />
          <h2 className="text-sm font-semibold">Live TV</h2>
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#06b6d4]/15 text-[#22d3ee] font-bold tracking-widest">
            PRO
          </span>
        </div>
        <div className="text-[10px] text-white/40" data-testid="pro-live-counter">
          {channelsForCategory.length} / {liveChannels.length}
        </div>
      </div>

      {/* v1.3.54 — Category bar moved to the TOP of the page, above the
          preview + EPG row. On Smart TVs the user couldn't reach the bar
          when it sat between the EPG and the channel list (D-pad UP
          collapsed straight from channel list into the EPG and skipped
          the bar entirely). Putting it at the very top guarantees a
          single D-pad UP from the channel list lands on it, and the
          preview/EPG never gets in the way.
          v1.3.56 — Compacted to a single row (tip merged into the
          Browse button title) so the EPG window has more vertical
          breathing room on 720p TVs where every pixel counts. */}
      {/* v1.4.14 — Category bar removed per user request.
          Category picker now lives in the channel-list panel header
          (right column) — see below. The keyboard UP-warp that used
          to jump focus back to the top bar is also gone; scrolling
          up on the channel list now just stops at the first row. */}

      {/* v1.4.6 — NEW LAYOUT
          Left column (stacked): preview on top, EPG below.
          Right column: channel list fills the full height.
          Requested by user: "epg window needs to move under preview screen
          and channel list where epg window is". */}
      <div className="flex items-stretch gap-3 p-3 flex-1 min-h-0 overflow-hidden">
        {/* ─── LEFT COLUMN: preview + EPG stacked ─── */}
        <div className="flex flex-col gap-3 flex-1 min-w-0 min-h-0" style={{ maxWidth: "50%" }}>
          <div
            ref={placeholderRef}
            data-testid="pro-live-preview"
            className="shrink-0 bg-black rounded-lg overflow-hidden relative"
            style={{ height: "40%", minHeight: 200 }}
          >
          {selected ? (
            native ? (
              // v1.4.0 rev 8 — Native (Android): the ExoPlayer/VLC
              // SurfaceView renders ON TOP of this placeholder ~350 ms
              // after the user pauses on a channel. Below the surface
              // we render a static logo + Watch button so:
              //   • during the debounce → the pane looks alive (logo)
              //   • if the user zaps quickly (surface never mounts) →
              //     the underlay stays visible, nothing flashes
              //   • Watch button is always reachable for D-pad users
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/50 text-xs p-4 text-center">
                {selected.logo_url ? (
                  <div className="max-h-[40%] max-w-[45%] opacity-60">
                    <PosterImage
                      src={selected.logo_url}
                      alt=""
                      fallbackIcon={Tv}
                      fallbackIconClassName="w-10 h-10 text-white/30"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <Tv className="w-10 h-10 text-white/30" />
                )}
                <div className="text-white/70 text-sm font-medium truncate max-w-full px-4">
                  {selected.name}
                </div>
                <button
                  onClick={() => onChannelOpen(selected)}
                  data-testid="pro-live-open-fullscreen"
                  className="px-3 py-1.5 rounded bg-[#06b6d4] hover:bg-[#22d3ee] text-black text-[11px] font-semibold flex items-center gap-1.5"
                >
                  <Play className="w-3 h-3 fill-current" /> Watch fullscreen
                </button>
              </div>
            ) : electron || webPlayer ? (
              // v1.4.1 — Electron (Windows) / v1.4.7 — Web Player: hls.js
              // plays HLS inline inside a real <video> tag. Non-HLS
              // (raw .ts) uses mpegts.js on Web, or falls through to the
              // "Watch fullscreen (mpv)" prompt on Electron.
              <>
                <video
                  ref={videoRef}
                  data-testid="pro-live-inline-video"
                  className="absolute inset-0 w-full h-full object-contain bg-black"
                  playsInline
                />
                <div
                  className={`absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/50 text-xs p-4 text-center pointer-events-none ${
                    isHlsStream || webPlayer ? "opacity-0" : "opacity-100"
                  }`}
                  style={{ transition: "opacity 200ms" }}
                >
                  {selected.logo_url ? (
                    <div className="max-h-[40%] max-w-[45%] opacity-60">
                      <PosterImage
                        src={selected.logo_url}
                        alt=""
                        fallbackIcon={Tv}
                        fallbackIconClassName="w-10 h-10 text-white/30"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ) : (
                    <Tv className="w-10 h-10 text-white/30" />
                  )}
                  <div className="text-white/70 text-sm font-medium truncate max-w-full px-4">
                    {selected.name}
                  </div>
                  {!isHlsStream && !webPlayer && (
                    <div className="text-[10px] text-white/40">MPEG-TS stream — press Watch for fullscreen mpv</div>
                  )}
                </div>
                <button
                  onClick={() => onChannelOpen(selected)}
                  data-testid="pro-live-open-fullscreen"
                  className="absolute bottom-2 right-2 px-3 py-1.5 rounded bg-[#06b6d4] hover:bg-[#22d3ee] text-black text-[11px] font-semibold flex items-center gap-1.5 shadow-lg"
                >
                  <Play className="w-3 h-3 fill-current" /> Watch
                </button>
              </>
            ) : previewChannel && previewChannel.stream_url === selected.stream_url ? (
              // Plain Web: user tapped Preview but no native or Electron
              // engine — Watch is essentially a no-op fallback.
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/40 text-xs p-4 text-center">
                <Play className="w-6 h-6" />
                <div>Preview not shown here — press Open to start playback.</div>
                <button
                  onClick={() => onChannelOpen(selected)}
                  data-testid="pro-live-play-fallback"
                  className="mt-1 px-3 py-1.5 rounded bg-[#06b6d4] text-black text-[11px] font-semibold"
                >
                  Open in native engine
                </button>
              </div>
            ) : (
              // Plain Web default: explicit-click preview.
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/50 text-xs p-4 text-center">
                {selected.logo_url ? (
                  <div className="max-h-[45%] max-w-[45%] opacity-70">
                    <PosterImage
                      src={selected.logo_url}
                      alt=""
                      fallbackIcon={Tv}
                      fallbackIconClassName="w-10 h-10 text-white/30"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <Tv className="w-10 h-10 text-white/30" />
                )}
                <div className="text-white/70 text-sm font-medium truncate max-w-full px-4">
                  {selected.name}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewChannelId(selected.stream_url)}
                    data-testid="pro-live-start-preview"
                    className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/15 border border-white/15 text-white text-[11px] font-semibold flex items-center gap-1.5"
                  >
                    <Play className="w-3 h-3" /> Preview
                  </button>
                  <button
                    onClick={() => onChannelOpen(selected)}
                    data-testid="pro-live-open-fullscreen"
                    className="px-3 py-1.5 rounded bg-[#06b6d4] hover:bg-[#22d3ee] text-black text-[11px] font-semibold flex items-center gap-1.5"
                  >
                    <Play className="w-3 h-3 fill-current" /> Watch
                  </button>
                </div>
                <div className="text-[10px] text-white/30 max-w-[80%]">
                  Streams open only when you tap Preview or Watch — protects your provider connection limit.
                </div>
              </div>
            )
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/30 text-xs">
              Select a channel to preview
            </div>
          )}
        </div>
        <div
          className="flex-1 min-w-0 min-h-0 bg-[#0d1018] rounded-lg p-3 flex flex-col"
          data-testid="pro-live-epg"
        >
          <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
            <div className="text-sm font-semibold truncate">{selected?.name || "—"}</div>
            {selected ? (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={(e) => onToggleFav(selected, e)}
                  data-testid="pro-live-fav-toggle"
                  className={`p-1.5 rounded transition-colors ${
                    selectedIsFav
                      ? "text-yellow-400 hover:bg-white/5"
                      : "text-white/30 hover:text-yellow-300 hover:bg-white/5"
                  }`}
                  aria-label={selectedIsFav ? "Remove from favorites" : "Add to favorites"}
                >
                  <Star className="w-4 h-4" fill={selectedIsFav ? "currentColor" : "none"} />
                </button>
                <button
                  onClick={() => onChannelOpen(selected)}
                  data-testid="pro-live-play"
                  className="p-1.5 rounded text-[#22d3ee] hover:bg-white/5"
                  aria-label="Play fullscreen"
                >
                  <Play className="w-4 h-4" />
                </button>
              </div>
            ) : null}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
          {programmes.length === 0 ? (
            <div className="text-[11px] text-white/40 italic">No EPG data for this channel.</div>
          ) : (
            <div className="space-y-1.5">
              {programmes.map((p, i) => {
                const live = nowProg && nowProg.start_ts === p.start_ts;
                const pct = live ? progressPct(p) : 0;
                const isPast = p.stop_ts < Math.floor(Date.now() / 1000);
                const canCatchUp =
                  isPast && xtream?.creds && selected?.xc_id && selected?.tv_archive;
                const isNext = nextProgStartTs && p.start_ts === nextProgStartTs;
                return (
                  <button
                    key={`${p.start_ts}-${i}`}
                    onClick={() => onProgrammeClick(p)}
                    disabled={!live && !canCatchUp}
                    className={`w-full text-left px-2 py-1.5 rounded text-[11px] transition-colors ${
                      live
                        ? "bg-[#06b6d4]/15 text-white"
                        : canCatchUp
                          ? "hover:bg-white/5 text-white/75 cursor-pointer"
                          : "text-white/50 cursor-default"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {live ? (
                          <span className="shrink-0 px-1 py-px rounded text-[8px] font-bold tracking-wider bg-red-500 text-white">
                            NOW
                          </span>
                        ) : isNext ? (
                          <span className="shrink-0 px-1 py-px rounded text-[8px] font-bold tracking-wider bg-[#22d3ee]/25 text-[#22d3ee] border border-[#22d3ee]/50">
                            NEXT
                          </span>
                        ) : null}
                        <span className="font-medium truncate">{p.title || "—"}</span>
                      </div>
                      <span className="text-white/40 shrink-0 text-[10px]">
                        {fmtTime(p.start)}
                      </span>
                    </div>
                    {live ? (
                      <div className="mt-1 h-1 rounded bg-white/10 overflow-hidden">
                        <div className="h-full bg-[#22d3ee]" style={{ width: `${pct}%` }} />
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
          </div>
        </div>
        {/* end LEFT COLUMN */}
        </div>

        {/* ─── RIGHT COLUMN: channel list (fills full height) ─── */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          {/* v1.4.14 — Category header. Replaces the top category bar.
              Clicking (or Enter on the D-pad) opens the fullscreen
              category picker popup. The current category name + count
              are shown inline so the user always knows which list
              they're looking at without scrolling to the top. */}
          <button
            type="button"
            onClick={() => setCategoryPopupOpen(true)}
            data-testid="pro-live-category-header"
            tabIndex={0}
            onKeyDown={(e) => {
              // v1.4.31 — When focus sits on the Category header on a
              // Smart TV, pressing Down must land on the FIRST channel
              // button. Otherwise Chromium's spatial-nav grabs the
              // EPG programme list on the left and the user is stuck.
              if (e.key === "ArrowDown") {
                const first = channelListRef.current?.querySelector('[data-testid^="pro-live-row-"]');
                if (first) {
                  e.preventDefault();
                  e.stopPropagation();
                  try { first.focus({ preventScroll: false }); } catch { first.focus(); }
                }
              }
            }}
            className="shrink-0 mb-2 flex items-center justify-between gap-3 bg-[#0d1018] hover:bg-[#131826] focus:bg-[#131826] border border-white/5 hover:border-cyan-400/30 focus:border-cyan-400/50 rounded-lg px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-[#22d3ee] transition-colors"
          >
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">
                Category
              </div>
              <div className="text-sm font-semibold text-white truncate">
                {activeCategory === "__favorites__"
                  ? `★ Favorites (${favoriteUrls.size})`
                  : `${activeCategory} (${channelsForCategory.length})`}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-cyan-300 shrink-0 rotate-90" />
          </button>

          <div
            ref={channelListRef}
            className="bg-[#0d1018] rounded-lg overflow-y-auto min-h-0 flex-1"
            data-testid="pro-live-channels"
          >
            {channelsForCategory.length === 0 ? (
              <div className="p-4 text-[12px] text-white/40">
                {activeCategory === "__favorites__"
                  ? "No favorites yet. Tap the ⭐ on any channel to add one."
                  : "No channels in this category."}
              </div>
            ) : (
              <ul>
                {channelsForCategory.map((ch) => {
                  const live = currentProgramme(epgByChannel, ch);
                  const pct = progressPct(live);
                  const isSelected = selected?.id === ch.id;
                  const isFav = favoriteUrls.has(ch.stream_url);
                  return (
                    <li key={ch.id} data-channel-id={ch.id}>
                      <div
                        className={`group w-full flex items-center gap-3 px-3 py-2 text-left transition-colors border-l-2 ${
                          isSelected
                            ? "bg-[#06b6d4]/10 border-[#22d3ee]"
                            : "border-transparent hover:bg-white/5"
                        }`}
                      >
                        <button
                          onClick={() => (webPlayer ? handleChannelActivate(ch) : setSelected(ch))}
                          onDoubleClick={() => onChannelOpen(ch)}
                          onKeyDown={(e) => {
                            // v1.4.32 — Explicit D-pad handling on each
                            // channel button. Google TV / Chromium
                            // spatial-nav otherwise interprets ArrowDown
                            // as "closest downward focusable" and
                            // sometimes picks a programme row in the
                            // EPG panel to the LEFT instead of the next
                            // channel below. Preventing default here
                            // ensures the global keydown handler that
                            // advances `selected` is the only path.
                            // Match all known D-pad key codes across
                            // Sony / TCL / Chromecast remotes.
                            const isDpad =
                              e.key === "ArrowDown" || e.key === "ArrowUp" ||
                              e.key === "ArrowRight" || e.key === "Down" ||
                              e.key === "Up" || e.key === "Right" ||
                              e.keyCode === 40 || e.keyCode === 38 ||
                              e.keyCode === 39 || e.keyCode === 20 ||
                              e.keyCode === 19 || e.keyCode === 22;
                            if (isDpad) {
                              e.preventDefault();
                            }
                          }}
                          data-testid={`pro-live-row-${ch.id}`}
                          data-stream-url={ch.stream_url}
                          className="flex-1 min-w-0 flex items-center gap-3 text-left"
                        >
                          <div className="w-10 h-10 rounded bg-black/40 flex items-center justify-center shrink-0 overflow-hidden">
                            <PosterImage
                              src={ch.logo_url}
                              alt=""
                              fallbackIcon={Tv}
                              fallbackIconClassName="w-4 h-4 text-white/30"
                              className="w-full h-full object-contain p-1"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-medium truncate">{ch.name}</div>
                            <div className="text-[10px] text-white/40 truncate">
                              {live?.title || "—"}
                            </div>
                            {live ? (
                              <div className="mt-1 h-0.5 rounded bg-white/10 overflow-hidden">
                                <div className="h-full bg-[#22d3ee]" style={{ width: `${pct}%` }} />
                              </div>
                            ) : null}
                          </div>
                        </button>
                        <button
                          onClick={(e) => onToggleFav(ch, e)}
                          data-testid={`pro-live-fav-${ch.id}`}
                          className={`shrink-0 p-1.5 rounded transition-opacity ${
                            isFav
                              ? "text-yellow-400 opacity-100"
                              : "text-white/30 opacity-0 group-hover:opacity-100 hover:text-yellow-300"
                          }`}
                          aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
                        >
                          <Star className="w-3.5 h-3.5" fill={isFav ? "currentColor" : "none"} />
                        </button>
                        <button
                          onClick={() => onChannelOpen(ch)}
                          data-testid={`pro-live-open-${ch.id}`}
                          className="shrink-0 p-1 text-white/30 hover:text-white/70"
                          aria-label="Open channel"
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        {/* end RIGHT COLUMN */}
      </div>
      {/* end 2-COLUMN LAYOUT */}

      {/* v1.3.52 — Long-press OK opens this fullscreen category picker. */}
      {categoryPopupOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="pro-live-category-popup"
          onClick={() => setCategoryPopupOpen(false)}
        >
          <div
            className="bg-[#0d1018] border border-white/10 rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <span className="text-white text-base font-medium">Pick a category</span>
              <button
                type="button"
                onClick={() => setCategoryPopupOpen(false)}
                data-testid="pro-live-category-popup-close"
                className="text-white/40 hover:text-white text-xs px-2 py-1 rounded border border-white/10"
              >
                Close
              </button>
            </div>
            <div className="overflow-y-auto p-2" onKeyDown={(e) => {
              // v1.4.26 — Same D-pad focus manager used by the Series
              // sidebar in Browse.jsx. On Android TV, Chromium scrolls
              // the container on ArrowDown/Up instead of moving focus
              // between the category buttons, so the user could see
              // "AMC", "Apple TV+", … listed below but pressing Down
              // did nothing. Walk the button list and focus() the
              // next/previous one; focus auto-scrolls it into view.
              const key = e.key;
              if (
                key !== "ArrowDown" && key !== "ArrowUp" &&
                key !== "PageDown" && key !== "PageUp" &&
                key !== "Home" && key !== "End"
              ) return;
              const container = e.currentTarget;
              const focusables = Array.from(
                container.querySelectorAll('button:not([disabled])')
              );
              if (focusables.length === 0) return;
              const active = document.activeElement;
              const idx = focusables.indexOf(active);
              let next = idx;
              if (key === "ArrowDown") next = idx < 0 ? 0 : Math.min(focusables.length - 1, idx + 1);
              else if (key === "ArrowUp") next = idx < 0 ? focusables.length - 1 : Math.max(0, idx - 1);
              else if (key === "PageDown") next = idx < 0 ? 0 : Math.min(focusables.length - 1, idx + 6);
              else if (key === "PageUp") next = idx < 0 ? 0 : Math.max(0, idx - 6);
              else if (key === "Home") next = 0;
              else if (key === "End") next = focusables.length - 1;
              if (next === idx || next < 0) return;
              e.preventDefault();
              e.stopPropagation();
              const el = focusables[next];
              try { el.focus({ preventScroll: false }); } catch { el.focus(); }
              try { el.scrollIntoView({ block: "nearest" }); } catch { /* older WebViews */ }
            }}>
              {[
                { name: "__favorites__", label: `★ Favorites (${favoriteUrls.size})` },
                ...categories.map((c) => ({ name: c.name, label: `${c.name} (${c.count})` })),
              ].map((c, idx) => {
                const isActive = c.name === activeCategory;
                return (
                  <button
                    key={c.name}
                    type="button"
                    autoFocus={idx === 0}
                    onClick={() => {
                      setActiveCategory(c.name);
                      setCategoryPopupOpen(false);
                    }}
                    data-testid={`pro-live-category-popup-item-${c.name}`}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors mb-1 focus:outline-none focus:ring-2 focus:ring-[#22d3ee] ${
                      isActive
                        ? "bg-[#06b6d4]/15 text-[#22d3ee]"
                        : "text-white/80 hover:bg-white/5 focus:bg-white/10"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-2 border-t border-white/10 text-[11px] text-white/40 text-center">
              Hold OK ≥0.8 s any time to reopen this menu
            </div>
          </div>
        </div>
      )}

      {/* v1.3.53 — Hold-OK progress hint. Tiny pill that grows as the user
          keeps OK pressed; vanishes the instant the popup opens or the
          key is released. Only renders during the hold window. */}
      {holdProgress > 0 && !categoryPopupOpen && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-full bg-black/85 border border-cyan-400/40 text-cyan-200 text-xs flex items-center gap-2 pointer-events-none shadow-2xl"
          data-testid="pro-live-hold-ok-hint"
        >
          <span>Keep holding OK for Categories…</span>
          <div className="w-16 h-1 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full bg-cyan-400 transition-all duration-75 ease-linear"
              style={{ width: `${Math.round(holdProgress * 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
 